import { useCallback } from 'react'
import { canReissueMfaChallenge } from 'lib/helpers/canReissueMfaChallenge/canReissueMfaChallenge'
import { mfaChallengeGrant as mfaChallengeGrantStore } from 'lib/helpers/mfaChallengeGrantStorage/mfaChallengeGrantStorage'
import { useSetTokenCookie } from 'lib/hooks/useSetTokenCookie/useSetTokenCookie'
import { useSetDeviceTokenCookie } from 'lib/hooks/useSetDeviceTokenCookie/useSetDeviceTokenCookie'
import { resolveLocalStorageBusinessId } from 'routes/helpers/resolveLocalStorageBusinessId/resolveLocalStorageBusinessId'
import type { AuthResponseHandlerHook_AuthResponseFragment } from './useAuthResponseHandler.graphql'
import {
  writeMfaStorage,
  routeToLoginSetup,
  routeToLoginVerify,
} from './helpers/persistMfaStorageAndRoute/persistMfaStorageAndRoute'

interface AuthResponseData<T extends AuthResponseHandlerHook_AuthResponseFragment> {
  authResponse: Maybe<T>
  email: string
  next?: Maybe<string>
}

interface AuthResponseHandlers<T extends AuthResponseHandlerHook_AuthResponseFragment> {
  onAuthenticated: (profile: Maybe<T['profile']>) => unknown
}

export const useAuthResponseHandler = () => {
  const setTokenCookie = useSetTokenCookie()
  const setDeviceTokenCookie = useSetDeviceTokenCookie()

  const setTokenCookies = useCallback(
    ({ token, deviceToken }: { token: Maybe<string>; deviceToken: Maybe<string> }) => {
      if (token) {
        setTokenCookie(token)
      }

      if (deviceToken) {
        setDeviceTokenCookie(deviceToken)
      }
    },
    [setTokenCookie, setDeviceTokenCookie],
  )

  return async <T extends AuthResponseHandlerHook_AuthResponseFragment>(
    { authResponse, email, next }: AuthResponseData<T>,
    { onAuthenticated }: AuthResponseHandlers<T>,
  ): Promise<boolean> => {
    const {
      token,
      profile,
      mfaChallengeGrant,
      deviceToken,
      mfaSetupRequired,
      availableMethods,
      mfaVerificationRequired,
    } = authResponse ?? {}

    resolveLocalStorageBusinessId(profile)

    if (mfaChallengeGrant) {
      mfaChallengeGrantStore.set(mfaChallengeGrant)
    } else {
      mfaChallengeGrantStore.clear()
    }

    const authenticate = async () => {
      setTokenCookies({ token, deviceToken })
      await onAuthenticated(profile)
      return true
    }

    // Enforced challenges re-mint at the destination from a held credential; route only when one is held.
    const canReissueChallenge = canReissueMfaChallenge()

    // Only the routing branches persist — the fall-through paths must leave storage untouched so a
    // response the page won't act on can't seed a stale email or method list.
    const persistAndRoute = (route: typeof routeToLoginSetup): true => {
      writeMfaStorage({ email, availableMethods })
      route({ next })
      return true
    }

    if (mfaSetupRequired && canReissueChallenge) {
      return persistAndRoute(routeToLoginSetup)
    }

    if (mfaVerificationRequired && canReissueChallenge) {
      return persistAndRoute(routeToLoginVerify)
    }

    // Authenticated nudge with no held credential: the page self-mints its enrollment challenge via
    // createTotp, so route on the session alone. Set cookies first so the page sees an authenticated
    // user (and Skip leaves a valid session).
    if (mfaSetupRequired && token) {
      setTokenCookies({ token, deviceToken })
      return persistAndRoute(routeToLoginSetup)
    }

    return token ? await authenticate() : false
  }
}
